import { type ApiResponse, type ApisauceConfig, create } from 'apisauce';

import { serializeQS } from '../helpers/serialize';

export type TApisauceResponse<T> = Omit<ApiResponse<T>, 'originalError'>;
export type TApisauceRequest = Omit<
  ApisauceConfig,
  'baseURL' | 'axiosInstance'
>;
type TApiConfig = ApisauceConfig;

const omitOriginalErrorInResponse = <T = unknown, E = T>(
  payload: ApiResponse<T, E>,
): Omit<ApiResponse<T, E>, 'originalError'> => {
  const { originalError: _originalError, ...rest } = payload;
  return rest;
};

export const api = <T>(apiSauceConfig?: TApiConfig) => {
  const instance = create({
    paramsSerializer: {
      serialize: serializeQS,
    },
    baseURL: '',
    ...(apiSauceConfig || {}),
  });

  return {
    get: async (url: string, params: any, apiConfig: TApisauceRequest) => {
      const response = await instance.get<T>(url, params, apiConfig);
      return omitOriginalErrorInResponse(response);
    },
    post: async (url: string, data: any, apiConfig: TApisauceRequest) => {
      const response = await instance.post<T>(url, data, apiConfig);
      return omitOriginalErrorInResponse(response);
    },
    put: async (url: string, data: any, apiConfig: TApisauceRequest) => {
      const response = await instance.put<T>(url, data, apiConfig);
      return omitOriginalErrorInResponse(response);
    },
    delete: async (url: string, data: any, apiConfig: TApisauceRequest) => {
      const response = await instance.delete<T>(url, data, apiConfig);
      return omitOriginalErrorInResponse(response);
    },
    any: async (apiConfig: TApisauceRequest) => {
      const response = await instance.any<T>(apiConfig);
      return omitOriginalErrorInResponse(response);
    },
    request: async <U = unknown, E = U>(apiConfig: TApisauceRequest) => {
      const response = await instance.any<U, E>(apiConfig);
      return omitOriginalErrorInResponse(response);
    },
    transform: {
      request: instance.addAsyncRequestTransform,
      response: instance.addAsyncRequestTransform,
    },
  };
};

export type TApiInstance = ReturnType<typeof api>;
